import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Check, Sparkles, ArrowRight, Crown } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ParentPricing() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      
      // If already subscribed as parent, redirect to portal
      if (response.data.subscription_tier === 'parent') {
        toast.success('You already have Parent Portal access!');
        navigate('/parent');
      }
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const handleGetAccess = () => {
    if (!user) {
      toast.error('Please sign in first');
      navigate('/login?redirect=/parent/pricing');
      return;
    }
    
    // Redirect to payment page with parent plan
    navigate('/payment?plan=parent');
  };

  const features = [
    'AI Worksheet Generator - Unlimited',
    'AI Quiz Generator - Unlimited',
    'AI Test Generator - Unlimited',
    'Topic Explainer - Unlimited',
    'AI Activity Suggester - Unlimited',
    'My Resources Folder',
    'Save & Download All Materials',
    'Mobile-Optimized Tools',
    'Support Your Child at Home'
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
        <Navigation />
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <Navigation />
      
      <div className="px-4 py-12 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl mb-4 shadow-lg">
              <Sparkles className="w-8 h-8 text-white" strokeWidth={2} />
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Parent Portal Access
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Support your child's learning with unlimited access to AI-powered educational tools
            </p>
          </div>

          {/* Pricing Card */}
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border-2 border-blue-200">
            {/* Badge */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white text-center py-3">
              <span className="font-bold text-sm uppercase tracking-wide">Monthly Subscription</span>
            </div>

            <div className="p-8 sm:p-12">
              {/* Price */}
              <div className="text-center mb-8">
                <div className="flex items-baseline justify-center gap-2 mb-2">
                  <span className="text-5xl sm:text-6xl font-bold text-gray-900">$10</span>
                  <span className="text-xl text-gray-600">/month</span>
                </div>
                <p className="text-gray-600">Cancel anytime • No long-term commitment</p>
              </div>

              {/* Features */}
              <div className="space-y-4 mb-8">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Everything Included:</h3>
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-green-600" strokeWidth={3} />
                    </div>
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>

              {/* CTA Button */}
              <button
                onClick={handleGetAccess}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl px-8 py-4 font-bold text-lg shadow-lg hover:shadow-xl transition-all active:scale-95 flex items-center justify-center gap-2"
              >
                <Crown className="w-6 h-6" />
                Subscribe for $10/month
                <ArrowRight className="w-5 h-5" />
              </button>

              {!user && (
                <p className="text-center text-sm text-gray-500 mt-4">
                  You'll be asked to sign in before payment
                </p>
              )}

              {/* Payment Methods */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <p className="text-center text-sm text-gray-600 mb-3">
                  We accept:
                </p>
                <div className="flex flex-wrap justify-center gap-3 text-sm text-gray-600">
                  <span className="px-4 py-2 bg-gray-50 rounded-lg">Airtel Money</span>
                  <span className="px-4 py-2 bg-gray-50 rounded-lg">UPI</span>
                  <span className="px-4 py-2 bg-gray-50 rounded-lg">Cash</span>
                </div>
              </div>
            </div>
          </div>

          {/* Why Parent Portal */}
          <div className="mt-12 bg-white rounded-2xl p-8 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              Why Parent Portal?
            </h3>
            <div className="grid sm:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Sparkles className="w-6 h-6 text-blue-600" />
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Save Time</h4>
                <p className="text-sm text-gray-600">
                  Generate worksheets and activities in minutes, not hours
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Crown className="w-6 h-6 text-purple-600" />
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Quality Learning</h4>
                <p className="text-sm text-gray-600">
                  AI-powered tools designed by an experienced teacher
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Check className="w-6 h-6 text-pink-600" />
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Flexible</h4>
                <p className="text-sm text-gray-600">
                  Monthly subscription. Cancel anytime with no penalties
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default ParentPricing;
